import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import createGlobe from 'cobe';
import { getCoords } from '../../utils/airportCoords';
import { projectToScreen, greatCirclePoints, phiForLongitude } from '../../utils/mapProjection';
import { haptic, hapticMedium } from '../../utils/haptics';
import { Loader2 } from 'lucide-react';

interface AnimFlight {
  dep: string;
  arr: string;
  flightNumber?: string;
  fromCoords: { lat: number; lon: number };
  toCoords: { lat: number; lon: number };
}

interface ImportGlobeProps {
  /** Parsed flights to animate onto the globe (only type=flight with airports) */
  flights: { dep: string; arr: string; flightNumber?: string }[];
  baseAirport: string;
  /** True while AI is still parsing the roster */
  isLoading: boolean;
  /** Called when the reveal animation finishes */
  onComplete: () => void;
}

/** Accelerating drumroll: 800ms down to 150ms */
function getRevealDelay(index: number): number {
  return Math.max(150, Math.round(800 * Math.pow(0.82, index)));
}

export function ImportGlobe({ flights, baseAirport, isLoading, onComplete }: ImportGlobeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const arcCanvasRef = useRef<HTMLCanvasElement>(null);

  const [revealedCount, setRevealedCount] = useState(0);
  const [phase, setPhase] = useState<'loading' | 'animating' | 'complete'>('loading');

  // --- Animation refs (mutated by timers, read every frame by onRender) ---
  const revealedRef = useRef(0);
  const animStartRef = useRef(0);
  const animDurationRef = useRef(800);
  const phaseRef = useRef<'loading' | 'animating' | 'complete'>('loading');

  const animatingIndexRef = useRef(-1); // which flight is currently tracing (-1 = none yet)

  const currentPhiRef = useRef(0);
  const targetPhiRef = useRef(0);

  // Stable ref for onComplete to avoid re-triggering effect
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Resolve flight coords
  const resolved = useMemo(
    () =>
      flights
        .map((f) => {
          const from = getCoords(f.dep);
          const to = getCoords(f.arr);
          if (!from || !to) return null;
          return { ...f, fromCoords: from, toCoords: to } as AnimFlight;
        })
        .filter((f): f is AnimFlight => f !== null),
    [flights],
  );
  const resolvedRef = useRef(resolved);
  resolvedRef.current = resolved;

  const baseCoords = getCoords(baseAirport);
  const initialPhi = baseCoords ? phiForLongitude(baseCoords.lon) : -Math.PI / 2;
  const initialTheta = baseCoords
    ? Math.max(-0.5, Math.min(0.5, ((baseCoords.lat * Math.PI) / 180) * 0.4))
    : 0.2;

  // --- Start animation chain when loading finishes ---
  useEffect(() => {
    if (isLoading) return;

    // No resolvable flights — skip animation entirely
    if (resolved.length === 0) {
      onCompleteRef.current();
      return;
    }

    phaseRef.current = 'animating';
    setPhase('animating');
    revealedRef.current = 0;
    setRevealedCount(0);

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    function revealNext(index: number) {
      if (cancelled) return;

      if (index >= resolved.length) {
        // All flights revealed — let the user admire the network
        phaseRef.current = 'complete';
        setPhase('complete');
        setRevealedCount(resolved.length);
        animatingIndexRef.current = -1;
        // Completion haptic — triple tap pattern
        try {
          navigator?.vibrate?.([15, 40, 15, 40, 30]);
        } catch {
          /* ignore */
        }
        // Linger for 2.5s with a slow victory spin before moving on
        timeoutId = setTimeout(() => {
          if (!cancelled) onCompleteRef.current();
        }, 2500);
        return;
      }

      const delay = getRevealDelay(index);
      animDurationRef.current = delay;
      animStartRef.current = performance.now();
      animatingIndexRef.current = index;

      // Rotate globe to face this route's midpoint
      const flight = resolved[index];
      const midLon = (flight.fromCoords.lon + flight.toCoords.lon) / 2;
      targetPhiRef.current = phiForLongitude(midLon);

      // Haptic feedback: strong for first 3, light for 4-6, silent after
      if (index < 3) hapticMedium();
      else if (index < 6) haptic();

      timeoutId = setTimeout(() => {
        if (cancelled) return;
        revealedRef.current = index + 1;
        setRevealedCount(index + 1);
        revealNext(index + 1);
      }, delay);
    }

    // Brief pause to let the globe settle before starting
    timeoutId = setTimeout(() => revealNext(0), 400);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isLoading, resolved]);

  // --- Overlay drawing (called every frame by cobe onRender) ---
  const drawOverlay = useCallback(
    (canvas: HTMLCanvasElement, phi: number, theta: number, size: number) => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const allFlights = resolvedRef.current;
      const revealed = revealedRef.current;
      const now = performance.now();

      // Always draw base airport with animated pulse
      if (baseCoords) {
        const { x, y, visible } = projectToScreen(baseCoords.lat, baseCoords.lon, phi, theta, size);
        if (visible) {
          const px = x * dpr;
          const py = y * dpr;
          const pulse = 0.5 + 0.5 * Math.sin(now * 0.003);

          ctx.beginPath();
          ctx.arc(px, py, (5 + pulse * 3) * dpr, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(220, 80, 70, ${0.2 + pulse * 0.15})`;
          ctx.lineWidth = 1.5 * dpr;
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(px, py, 5 * dpr, 0, Math.PI * 2);
          ctx.fillStyle = '#dc5046';
          ctx.fill();

          // Base label
          ctx.font = `600 ${10 * dpr}px "DM Sans", sans-serif`;
          ctx.fillStyle = 'rgba(255, 200, 190, 0.9)';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(baseAirport, px + 5 * dpr + 4 * dpr, py);
        }
      }

      // Fully revealed arcs + dots (skip base airport — already drawn above)
      for (let i = 0; i < Math.min(revealed, allFlights.length); i++) {
        drawArc(ctx, allFlights[i], phi, theta, size, dpr, 1.0);
        if (allFlights[i].arr !== baseAirport) {
          drawDot(ctx, allFlights[i].toCoords, allFlights[i].arr, phi, theta, size, dpr);
        }
        if (allFlights[i].dep !== baseAirport) {
          drawDot(ctx, allFlights[i].fromCoords, allFlights[i].dep, phi, theta, size, dpr);
        }
      }

      // Currently tracing arc (partial) — only when revealNext has started this flight
      const animIdx = animatingIndexRef.current;
      if (phaseRef.current === 'animating' && animIdx === revealed && revealed < allFlights.length) {
        const elapsed = now - animStartRef.current;
        const t = Math.min(1, elapsed / animDurationRef.current);
        const eased = 1 - Math.pow(1 - t, 2); // ease-out
        drawArc(ctx, allFlights[revealed], phi, theta, size, dpr, eased);

        // Draw a bright leading dot at the arc tip
        if (eased > 0.05) {
          drawLeadingDot(ctx, allFlights[revealed], phi, theta, size, dpr, eased);
        }
      }
    },
    [baseAirport, baseCoords],
  );

  // --- cobe globe setup ---
  useEffect(() => {
    const canvas = canvasRef.current;
    const arcCanvas = arcCanvasRef.current;
    if (!canvas || !arcCanvas) return;

    const container = canvas.parentElement;
    if (!container) return;

    const size = container.clientWidth;
    const dpr = window.devicePixelRatio || 1;
    arcCanvas.width = size * dpr;
    arcCanvas.height = size * dpr;
    arcCanvas.style.width = `${size}px`;
    arcCanvas.style.height = `${size}px`;

    currentPhiRef.current = initialPhi;
    targetPhiRef.current = initialPhi;

    const markers = baseCoords
      ? [{ location: [baseCoords.lat, baseCoords.lon] as [number, number], size: 0.04 }]
      : [];

    const samplesForDpr = dpr >= 2 ? 40000 : dpr >= 1.5 ? 28000 : 18000;

    let globe: ReturnType<typeof createGlobe>;
    try {
      globe = createGlobe(canvas, {
        devicePixelRatio: dpr,
        width: size * dpr,
        height: size * dpr,
        phi: initialPhi,
        theta: initialTheta,
        dark: 1,
        diffuse: 3,
        mapSamples: samplesForDpr,
        mapBrightness: 8,
        mapBaseBrightness: dpr >= 2 ? 0 : 0.07,
        baseColor: [0.4, 0.33, 0.25],
        markerColor: [0.86, 0.31, 0.27],
        glowColor: [0.15, 0.18, 0.25],
        markers,
        scale: 1,
        offset: [0, 0],
        onRender: (state) => {
          // Gentle auto-rotate during loading, faster victory spin after completion
          if (phaseRef.current === 'loading') {
            targetPhiRef.current -= 0.002;
          } else if (phaseRef.current === 'complete') {
            targetPhiRef.current -= 0.008;
          }
          // Smooth interpolation toward target rotation
          currentPhiRef.current += (targetPhiRef.current - currentPhiRef.current) * 0.08;
          state.phi = currentPhiRef.current;
          state.theta = initialTheta;

          drawOverlay(arcCanvas, currentPhiRef.current, initialTheta, size);
        },
      });
    } catch {
      return;
    }

    return () => {
      globe?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseAirport]);

  const total = resolved.length;
  const statusText =
    phase === 'loading'
      ? 'Scanning your roster...'
      : phase === 'complete'
        ? `${total} flight${total !== 1 ? 's' : ''} found`
        : `${revealedCount} of ${total} flights`;

  return (
    <div className="flex flex-col items-center gap-3 py-2">
      <div className="relative w-full mx-auto" style={{ aspectRatio: '1', maxWidth: '320px' }}>
        <canvas ref={canvasRef} className="w-full h-full" style={{ contain: 'layout size' }} />
        <canvas ref={arcCanvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
      </div>

      <div className="flex items-center gap-2 text-sm font-medium text-slate-200">
        {phase === 'loading' && <Loader2 size={14} className="animate-spin text-accent" />}
        {phase === 'complete' && (
          <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
        )}
        {statusText}
      </div>
    </div>
  );
}

// ── Drawing helpers ──

function drawArc(
  ctx: CanvasRenderingContext2D,
  flight: AnimFlight,
  phi: number,
  theta: number,
  size: number,
  dpr: number,
  progress: number,
) {
  const points = greatCirclePoints(
    flight.fromCoords.lat,
    flight.fromCoords.lon,
    flight.toCoords.lat,
    flight.toCoords.lon,
    80,
  );

  const drawCount = Math.ceil(points.length * progress);
  const segments: { x: number; y: number }[][] = [];
  let current: { x: number; y: number }[] = [];

  for (let i = 0; i < drawCount; i++) {
    const p = points[i];
    const { x, y, visible } = projectToScreen(p.lat, p.lon, phi, theta, size);
    if (visible) {
      current.push({ x: x * dpr, y: y * dpr });
    } else if (current.length > 0) {
      segments.push(current);
      current = [];
    }
  }
  if (current.length > 0) segments.push(current);

  for (const seg of segments) {
    if (seg.length < 2) continue;
    // Glow
    ctx.beginPath();
    ctx.moveTo(seg[0].x, seg[0].y);
    for (let i = 1; i < seg.length; i++) ctx.lineTo(seg[i].x, seg[i].y);
    ctx.strokeStyle = 'rgba(220, 80, 70, 0.25)';
    ctx.lineWidth = 6 * dpr;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();

    // Solid core
    ctx.beginPath();
    ctx.moveTo(seg[0].x, seg[0].y);
    for (let i = 1; i < seg.length; i++) ctx.lineTo(seg[i].x, seg[i].y);
    ctx.strokeStyle = 'rgba(220, 80, 70, 0.95)';
    ctx.lineWidth = 2 * dpr;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  }
}

/** Bright dot at the leading edge of a tracing arc */
function drawLeadingDot(
  ctx: CanvasRenderingContext2D,
  flight: AnimFlight,
  phi: number,
  theta: number,
  size: number,
  dpr: number,
  progress: number,
) {
  const points = greatCirclePoints(
    flight.fromCoords.lat,
    flight.fromCoords.lon,
    flight.toCoords.lat,
    flight.toCoords.lon,
    80,
  );
  const idx = Math.min(Math.floor(points.length * progress), points.length - 1);
  const p = points[idx];
  const { x, y, visible } = projectToScreen(p.lat, p.lon, phi, theta, size);
  if (!visible) return;

  const px = x * dpr;
  const py = y * dpr;

  // Bright glow
  ctx.beginPath();
  ctx.arc(px, py, 4 * dpr, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 120, 100, 0.6)';
  ctx.fill();

  // White-hot center
  ctx.beginPath();
  ctx.arc(px, py, 1.5 * dpr, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 220, 200, 0.9)';
  ctx.fill();
}

function drawDot(
  ctx: CanvasRenderingContext2D,
  coords: { lat: number; lon: number },
  iata: string,
  phi: number,
  theta: number,
  size: number,
  dpr: number,
) {
  const { x, y, visible } = projectToScreen(coords.lat, coords.lon, phi, theta, size);
  if (!visible) return;

  const px = x * dpr;
  const py = y * dpr;

  ctx.beginPath();
  ctx.arc(px, py, 2.5 * dpr, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(220, 80, 70, 0.7)';
  ctx.fill();

  ctx.font = `600 ${10 * dpr}px "DM Sans", sans-serif`;
  ctx.fillStyle = 'rgba(255, 200, 190, 0.9)';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(iata, px + 2.5 * dpr + 4 * dpr, py);
}
